import type { SkyMapNode } from './FlightPathPanel';

export type SkyMapMatrix3 = [
  [number, number, number],
  [number, number, number],
  [number, number, number],
];

export type SkyMapPoint2 = [number, number];
export type SkyMapPoint3 = [number, number, number];

export interface SkyMapPanoramaNavigation {
  version: 1;
  kind: 'ironview.sky-map.panorama-navigation';
  ground_normal: SkyMapPoint3;
  camera_height: number;
}

export interface GroundPanoramaMosaicCell {
  node: SkyMapNode;
  planePolygon: SkyMapPoint2[];
  worldPolygon: SkyMapPoint3[];
}

export interface GroundPanoramaMosaic {
  cells: GroundPanoramaMosaicCell[];
  planePerimeter: SkyMapPoint2[];
  worldPerimeter: SkyMapPoint3[];
}

export interface PanoramaViewport {
  width: number;
  height: number;
  pitch: number;
  yaw: number;
  hfov: number;
}

const EPSILON = 1e-7;

const dot = (left: readonly number[], right: readonly number[]) => (
  left[0] * right[0] + left[1] * right[1] + left[2] * right[2]
);

const cross3 = (left: SkyMapPoint3, right: SkyMapPoint3): SkyMapPoint3 => [
  left[1] * right[2] - left[2] * right[1],
  left[2] * right[0] - left[0] * right[2],
  left[0] * right[1] - left[1] * right[0],
];

const normalize3 = (value: SkyMapPoint3): SkyMapPoint3 | null => {
  const magnitude = Math.hypot(...value);
  if (magnitude <= EPSILON) return null;
  return value.map((component) => component / magnitude) as SkyMapPoint3;
};

const signedArea = (polygon: readonly SkyMapPoint2[]) => polygon.reduce(
  (area, point, index) => {
    const next = polygon[(index + 1) % polygon.length];
    return area + point[0] * next[1] - next[0] * point[1];
  },
  0,
) / 2;

const convexHull = (points: readonly SkyMapPoint2[]): SkyMapPoint2[] => {
  const sorted = [...points]
    .sort((left, right) => left[0] - right[0] || left[1] - right[1])
    .filter((point, index, values) => (
      index === 0
      || Math.hypot(point[0] - values[index - 1][0], point[1] - values[index - 1][1])
        > EPSILON
    ));
  if (sorted.length <= 2) return sorted;

  const turn = (origin: SkyMapPoint2, left: SkyMapPoint2, right: SkyMapPoint2) => (
    (left[0] - origin[0]) * (right[1] - origin[1])
    - (left[1] - origin[1]) * (right[0] - origin[0])
  );
  const lower: SkyMapPoint2[] = [];
  sorted.forEach((point) => {
    while (lower.length >= 2 && turn(lower.at(-2)!, lower.at(-1)!, point) <= EPSILON) {
      lower.pop();
    }
    lower.push(point);
  });
  const upper: SkyMapPoint2[] = [];
  [...sorted].reverse().forEach((point) => {
    while (upper.length >= 2 && turn(upper.at(-2)!, upper.at(-1)!, point) <= EPSILON) {
      upper.pop();
    }
    upper.push(point);
  });
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
};

const estimatePerimeterPadding = (
  points: readonly SkyMapPoint2[],
  cameraHeight: number,
) => {
  const stepLengths = points.slice(1)
    .map((point, index) => Math.hypot(
      point[0] - points[index][0],
      point[1] - points[index][1],
    ))
    .filter((distance) => distance > EPSILON)
    .sort((left, right) => left - right);
  const medianStep = stepLengths.length > 0
    ? stepLengths[Math.floor(stepLengths.length / 2)]
    : 0;
  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);
  const diagonal = Math.hypot(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...ys) - Math.min(...ys),
  );
  return Math.max(medianStep * 2, diagonal * 0.04, cameraHeight * 0.75);
};

const lineIntersection = (
  pointA: SkyMapPoint2,
  directionA: SkyMapPoint2,
  pointB: SkyMapPoint2,
  directionB: SkyMapPoint2,
): SkyMapPoint2 | null => {
  const denominator = directionA[0] * directionB[1] - directionA[1] * directionB[0];
  if (Math.abs(denominator) <= EPSILON) return null;
  const delta: SkyMapPoint2 = [pointB[0] - pointA[0], pointB[1] - pointA[1]];
  const scale = (delta[0] * directionB[1] - delta[1] * directionB[0])
    / denominator;
  return [
    pointA[0] + directionA[0] * scale,
    pointA[1] + directionA[1] * scale,
  ];
};

const expandConvexPolygon = (
  polygon: readonly SkyMapPoint2[],
  padding: number,
): SkyMapPoint2[] => polygon.map((point, index) => {
  const previous = polygon[(index - 1 + polygon.length) % polygon.length];
  const next = polygon[(index + 1) % polygon.length];
  const previousLength = Math.hypot(point[0] - previous[0], point[1] - previous[1]);
  const nextLength = Math.hypot(next[0] - point[0], next[1] - point[1]);
  const previousDirection: SkyMapPoint2 = [
    (point[0] - previous[0]) / previousLength,
    (point[1] - previous[1]) / previousLength,
  ];
  const nextDirection: SkyMapPoint2 = [
    (next[0] - point[0]) / nextLength,
    (next[1] - point[1]) / nextLength,
  ];
  const previousOffset: SkyMapPoint2 = [
    point[0] + previousDirection[1] * padding,
    point[1] - previousDirection[0] * padding,
  ];
  const nextOffset: SkyMapPoint2 = [
    point[0] + nextDirection[1] * padding,
    point[1] - nextDirection[0] * padding,
  ];
  const intersection = lineIntersection(
    previousOffset,
    previousDirection,
    nextOffset,
    nextDirection,
  );
  if (intersection && Math.hypot(
    intersection[0] - point[0],
    intersection[1] - point[1],
  ) <= padding * 4) {
    return intersection;
  }
  const outward: SkyMapPoint2 = [
    previousDirection[1] + nextDirection[1],
    -previousDirection[0] - nextDirection[0],
  ];
  const outwardLength = Math.hypot(...outward);
  if (outwardLength <= EPSILON) return previousOffset;
  return [
    point[0] + outward[0] / outwardLength * padding,
    point[1] + outward[1] / outwardLength * padding,
  ];
});

const buildLinearPerimeter = (
  points: readonly SkyMapPoint2[],
  padding: number,
): SkyMapPoint2[] => {
  const center = points.reduce<SkyMapPoint2>(
    (sum, point) => [sum[0] + point[0], sum[1] + point[1]],
    [0, 0],
  ).map((component) => component / points.length) as SkyMapPoint2;
  const covariance = points.reduce(
    (value, point) => {
      const x = point[0] - center[0];
      const y = point[1] - center[1];
      return { xx: value.xx + x * x, xy: value.xy + x * y, yy: value.yy + y * y };
    },
    { xx: 0, xy: 0, yy: 0 },
  );
  const angle = Math.atan2(2 * covariance.xy, covariance.xx - covariance.yy) / 2;
  const axis: SkyMapPoint2 = [Math.cos(angle), Math.sin(angle)];
  const perpendicular: SkyMapPoint2 = [-axis[1], axis[0]];
  const coordinates = points.map((point) => {
    const offset: SkyMapPoint2 = [point[0] - center[0], point[1] - center[1]];
    return [
      offset[0] * axis[0] + offset[1] * axis[1],
      offset[0] * perpendicular[0] + offset[1] * perpendicular[1],
    ] as SkyMapPoint2;
  });
  const along = coordinates.map((point) => point[0]);
  const across = coordinates.map((point) => point[1]);
  const bounds = {
    minAlong: Math.min(...along) - padding,
    maxAlong: Math.max(...along) + padding,
    minAcross: Math.min(...across) - padding,
    maxAcross: Math.max(...across) + padding,
  };
  return [
    [bounds.minAlong, bounds.minAcross],
    [bounds.maxAlong, bounds.minAcross],
    [bounds.maxAlong, bounds.maxAcross],
    [bounds.minAlong, bounds.maxAcross],
  ].map(([alongValue, acrossValue]) => [
    center[0] + axis[0] * alongValue + perpendicular[0] * acrossValue,
    center[1] + axis[1] * alongValue + perpendicular[1] * acrossValue,
  ]);
};

const clipToNearestSeedHalfPlane = (
  polygon: readonly SkyMapPoint2[],
  seed: SkyMapPoint2,
  other: SkyMapPoint2,
): SkyMapPoint2[] => {
  const normal: SkyMapPoint2 = [other[0] - seed[0], other[1] - seed[1]];
  if (Math.hypot(...normal) <= EPSILON) return [...polygon];
  const limit = (
    other[0] * other[0] + other[1] * other[1]
    - seed[0] * seed[0] - seed[1] * seed[1]
  ) / 2;
  const distance = (point: SkyMapPoint2) => normal[0] * point[0] + normal[1] * point[1] - limit;
  const output: SkyMapPoint2[] = [];

  polygon.forEach((current, index) => {
    const previous = polygon[(index - 1 + polygon.length) % polygon.length];
    const currentDistance = distance(current);
    const previousDistance = distance(previous);
    const currentInside = currentDistance <= EPSILON;
    const previousInside = previousDistance <= EPSILON;
    if (currentInside !== previousInside) {
      const interpolation = previousDistance / (previousDistance - currentDistance);
      output.push([
        previous[0] + (current[0] - previous[0]) * interpolation,
        previous[1] + (current[1] - previous[1]) * interpolation,
      ]);
    }
    if (currentInside) output.push(current);
  });
  return output;
};

export const isSkyMapPanoramaNavigation = (
  value: unknown,
): value is SkyMapPanoramaNavigation => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<SkyMapPanoramaNavigation>;
  return candidate.version === 1
    && candidate.kind === 'ironview.sky-map.panorama-navigation'
    && Array.isArray(candidate.ground_normal)
    && candidate.ground_normal.length === 3
    && candidate.ground_normal.every(Number.isFinite)
    && typeof candidate.camera_height === 'number'
    && Number.isFinite(candidate.camera_height)
    && candidate.camera_height > 0;
};

export const buildGroundPanoramaMosaic = (
  nodes: readonly SkyMapNode[],
  navigation: SkyMapPanoramaNavigation,
): GroundPanoramaMosaic | null => {
  const groundNormal = normalize3([...navigation.ground_normal]);
  if (!groundNormal) return null;
  const reference: SkyMapPoint3 = Math.abs(groundNormal[1]) < 0.9
    ? [0, 1, 0]
    : [1, 0, 0];
  const groundAxisX = normalize3(cross3(reference, groundNormal));
  if (!groundAxisX) return null;
  const groundAxisY = cross3(groundNormal, groundAxisX);
  const seeds = nodes.flatMap((node) => {
    if (!node.panorama || !node.reconstructionPosition) return [];
    const footprint = node.reconstructionPosition.map(
      (component, index) => component - groundNormal[index] * navigation.camera_height,
    ) as SkyMapPoint3;
    return [{
      node,
      footprint,
      point: [dot(footprint, groundAxisX), dot(footprint, groundAxisY)] as SkyMapPoint2,
    }];
  }).filter((seed, index, values) => !values.slice(0, index).some((candidate) => (
    Math.hypot(seed.point[0] - candidate.point[0], seed.point[1] - candidate.point[1])
      <= EPSILON
  )));
  if (seeds.length < 2) return null;

  const points = seeds.map((seed) => seed.point);
  const padding = estimatePerimeterPadding(points, navigation.camera_height);
  const hull = convexHull(points);
  const planePerimeter = hull.length >= 3 && Math.abs(signedArea(hull)) > EPSILON
    ? expandConvexPolygon(hull, padding)
    : buildLinearPerimeter(points, padding);
  const planeOffset = seeds.reduce(
    (sum, seed) => sum + dot(seed.footprint, groundNormal),
    0,
  ) / seeds.length;
  const toWorld = (point: SkyMapPoint2): SkyMapPoint3 => [
    groundAxisX[0] * point[0] + groundAxisY[0] * point[1] + groundNormal[0] * planeOffset,
    groundAxisX[1] * point[0] + groundAxisY[1] * point[1] + groundNormal[1] * planeOffset,
    groundAxisX[2] * point[0] + groundAxisY[2] * point[1] + groundNormal[2] * planeOffset,
  ];
  const cells = seeds.flatMap((seed) => {
    const planePolygon = seeds.reduce<SkyMapPoint2[]>((polygon, other) => (
      polygon.length === 0 || other === seed
        ? polygon
        : clipToNearestSeedHalfPlane(polygon, seed.point, other.point)
    ), [...planePerimeter]);
    if (planePolygon.length < 3 || Math.abs(signedArea(planePolygon)) <= EPSILON) return [];
    return [{
      node: seed.node,
      planePolygon,
      worldPolygon: planePolygon.map(toWorld),
    }];
  });

  return {
    cells,
    planePerimeter,
    worldPerimeter: planePerimeter.map(toWorld),
  };
};

type ViewPoint3 = { x: number; y: number; z: number };
type ScreenPoint = { x: number; y: number };

const clipViewPolygonToNearPlane = (
  polygon: readonly ViewPoint3[],
  nearPlane = 0.001,
): ViewPoint3[] => {
  const output: ViewPoint3[] = [];
  polygon.forEach((current, index) => {
    const previous = polygon[(index - 1 + polygon.length) % polygon.length];
    const currentInside = current.z >= nearPlane;
    const previousInside = previous.z >= nearPlane;
    if (currentInside !== previousInside) {
      const interpolation = (nearPlane - previous.z) / (current.z - previous.z);
      output.push({
        x: previous.x + (current.x - previous.x) * interpolation,
        y: previous.y + (current.y - previous.y) * interpolation,
        z: nearPlane,
      });
    }
    if (currentInside) output.push(current);
  });
  return output;
};

const clipScreenPolygon = (
  polygon: readonly ScreenPoint[],
  width: number,
  height: number,
): ScreenPoint[] => {
  const boundaries = [
    {
      inside: (point: ScreenPoint) => point.x >= 0,
      intersection: (start: ScreenPoint, end: ScreenPoint) => ({
        x: 0,
        y: start.y + (end.y - start.y) * (-start.x) / (end.x - start.x),
      }),
    },
    {
      inside: (point: ScreenPoint) => point.x <= width,
      intersection: (start: ScreenPoint, end: ScreenPoint) => ({
        x: width,
        y: start.y + (end.y - start.y) * (width - start.x) / (end.x - start.x),
      }),
    },
    {
      inside: (point: ScreenPoint) => point.y >= 0,
      intersection: (start: ScreenPoint, end: ScreenPoint) => ({
        x: start.x + (end.x - start.x) * (-start.y) / (end.y - start.y),
        y: 0,
      }),
    },
    {
      inside: (point: ScreenPoint) => point.y <= height,
      intersection: (start: ScreenPoint, end: ScreenPoint) => ({
        x: start.x + (end.x - start.x) * (height - start.y) / (end.y - start.y),
        y: height,
      }),
    },
  ];
  return boundaries.reduce<ScreenPoint[]>((points, boundary) => {
    const output: ScreenPoint[] = [];
    points.forEach((current, index) => {
      const previous = points[(index - 1 + points.length) % points.length];
      const currentInside = boundary.inside(current);
      const previousInside = boundary.inside(previous);
      if (currentInside !== previousInside) {
        output.push(boundary.intersection(previous, current));
      }
      if (currentInside) output.push(current);
    });
    return output;
  }, [...polygon]);
};

export const projectGroundPolygon = (
  worldPolygon: readonly SkyMapPoint3[],
  current: SkyMapNode,
  viewport: PanoramaViewport,
): string | null => {
  if (!current.reconstructionPosition || !current.worldToPanoramaRotation) return null;
  if (viewport.width <= 0 || viewport.height <= 0 || viewport.hfov <= 0) return null;
  const pitch = viewport.pitch * Math.PI / 180;
  const yaw = viewport.yaw * Math.PI / 180;
  const forward: SkyMapPoint3 = [
    Math.sin(yaw) * Math.cos(pitch),
    Math.sin(pitch),
    Math.cos(yaw) * Math.cos(pitch),
  ];
  const right: SkyMapPoint3 = [Math.cos(yaw), 0, -Math.sin(yaw)];
  const up = cross3(forward, right);
  const viewPolygon = worldPolygon.map((worldPoint): ViewPoint3 => {
    const worldDirection = worldPoint.map(
      (component, index) => component - current.reconstructionPosition![index],
    ) as SkyMapPoint3;
    const panoramaDirection = current.worldToPanoramaRotation!.map((row) => (
      dot(row, worldDirection)
    )) as SkyMapPoint3;
    const pannellumDirection: SkyMapPoint3 = [
      panoramaDirection[0],
      -panoramaDirection[1],
      panoramaDirection[2],
    ];
    return {
      x: dot(pannellumDirection, right),
      y: dot(pannellumDirection, up),
      z: dot(pannellumDirection, forward),
    };
  });
  const visiblePolygon = clipViewPolygonToNearPlane(viewPolygon);
  if (visiblePolygon.length < 3) return null;
  const focalLength = viewport.width
    / (2 * Math.tan(viewport.hfov * Math.PI / 360));
  const projected = visiblePolygon.map((point): ScreenPoint => ({
    x: viewport.width / 2 + focalLength * point.x / point.z,
    y: viewport.height / 2 - focalLength * point.y / point.z,
  })).filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  const clipped = clipScreenPolygon(projected, viewport.width, viewport.height);
  if (clipped.length < 3) return null;
  const area = Math.abs(clipped.reduce((sum, point, index) => {
    const next = clipped[(index + 1) % clipped.length];
    return sum + point.x * next.y - next.x * point.y;
  }, 0) / 2);
  if (!Number.isFinite(area) || area < 0.5) return null;
  return `${clipped.map((point, index) => (
    `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)} ${point.y.toFixed(2)}`
  )).join('')}Z`;
};

export const projectGroundMosaicCell = (
  cell: GroundPanoramaMosaicCell,
  current: SkyMapNode,
  viewport: PanoramaViewport,
): string | null => projectGroundPolygon(cell.worldPolygon, current, viewport);
